/**
 * BottomTabBar — mobile navigation, Apple HIG + Network-iA signature.
 *
 * Layout :
 *  - Floating pill with safe-area bottom respect
 *  - 5 tabs : Home / Audit / Pipeline / Studio / More (drawer)
 *  - Each tab : fixed-size cell (h-14) so labels never wrap, pill aligns
 *  - Icons : Lucide stroke 2.25 constant, size 22 — SF-Symbol-like density
 *  - Active : pill colored (tinted gradient + soft glow), icon fill, label bold
 *  - Tap : active:scale-95 spring (haptic feel)
 *
 * Drawer "More" : bottom-sheet draggable to dismiss + 6 secondary entries.
 */
import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  UserSearch,
  ClipboardList,
  Wand2,
  Plus,
  X,
  CalendarDays,
  Compass,
  BookOpen,
  Palette,
  BellRing,
  User as UserIcon,
  LogOut,
  ChevronRight,
  Sparkles,
  Target,
  Bot,
  MessageSquare,
  Settings2,
  Plug,
  Cable,
  Workflow,
  Zap,
  LayoutTemplate,
  Package,
  BarChart3,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useFeatures } from '@/hooks/useFeatures';
import { useAutomationsAccess } from '@/lib/automations';

interface Tab {
  to: string;
  /** Short i18n key (mobile label). */
  labelKey: string;
  icon: typeof Home;
  tint: string;
}

// 4 onglets principaux alignés sur les 4 jobs Network-iA :
// home · prospecter · inéa · contenu. Le 5e bouton "Plus" ouvre le drawer
// avec TOUS les sous-menus de chaque groupe.
const PRIMARY_TABS: Tab[] = [
  { to: '/', labelKey: 'nav.shortDashboard', icon: Home, tint: '#a855f7' },
  { to: '/audit', labelKey: 'nav.groups.prospecting', icon: Target, tint: '#ec4899' },
  { to: '/conversations', labelKey: 'nav.groups.inea', icon: Bot, tint: '#8b5cf6' },
  { to: '/studio', labelKey: 'nav.groups.content', icon: Sparkles, tint: '#a855f7' },
];

// Lot 7 (2026-10-29) — espace M&R : accueil · contenu · conversations · automatisations (comptes connectés si le module n'est pas ouvert).
const MR_TABS = (automations: boolean): Tab[] => [
  { to: '/', labelKey: 'nav.shortDashboard', icon: Home, tint: '#a855f7' },
  { to: '/contenu', labelKey: 'nav.groups.content', icon: Sparkles, tint: '#a855f7' },
  { to: '/conversations', labelKey: 'nav.conversations', icon: MessageSquare, tint: '#06b6d4' },
  automations
    ? { to: '/automatisations', labelKey: 'nav.automations', icon: Zap, tint: '#8b5cf6' }
    : { to: '/comptes', labelKey: 'nav.accounts', icon: Plug, tint: '#10b981' },
];

interface DrawerEntry {
  to: string;
  labelKey: string;
  icon: typeof Home;
  tint: string;
  /** Optional group label shown above this entry (visual section header). */
  groupKey?: string;
}

// Drawer aligné sur la nouvelle architecture : sous-menus regroupés par
// thème, headers visuels intercalés (ex: "Prospection" avant audit/pipeline/relances).
const DRAWER_ENTRIES: DrawerEntry[] = [
  // ── Prospection ────────────────────────────────────────────
  { to: '/audit', labelKey: 'nav.audit', icon: UserSearch, tint: '#ec4899', groupKey: 'nav.groups.prospecting' },
  { to: '/prospects', labelKey: 'nav.pipeline', icon: ClipboardList, tint: '#a855f7' },
  { to: '/relances', labelKey: 'nav.followups', icon: BellRing, tint: '#f59e0b' },
  { to: '/sequences', labelKey: 'nav.sequences', icon: Workflow, tint: '#8b5cf6' },
  // ── Contenu & Marque ──────────────────────────────────────
  { to: '/contenu', labelKey: 'nav.content', icon: CalendarDays, tint: '#f59e0b', groupKey: 'nav.groups.content' },
  { to: '/studio', labelKey: 'nav.studio', icon: Wand2, tint: '#ec4899' },
  { to: '/bibliotheque', labelKey: 'nav.library', icon: BookOpen, tint: '#06b6d4' },
  { to: '/identite', labelKey: 'nav.identity', icon: Palette, tint: '#3b82f6' },
  // ── Inéa & Coach ──────────────────────────────────────────
  { to: '/diagnostic', labelKey: 'nav.diagnostic', icon: Compass, tint: '#a855f7', groupKey: 'nav.groups.inea' },
  { to: '/conversations', labelKey: 'nav.conversations', icon: MessageSquare, tint: '#a855f7' },
  { to: '/parametres/inea', labelKey: 'nav.ineaSettings', icon: Settings2, tint: '#3b82f6' },
  // ── Automatisations (lot 1a, 2026-10-28 ; drapeau automations_enabled) ──
  { to: '/automatisations', labelKey: 'nav.automations', icon: Zap, tint: '#8b5cf6', groupKey: 'nav.groups.automations' },
  { to: '/automatisations/modeles', labelKey: 'nav.automationTemplates', icon: LayoutTemplate, tint: '#a855f7' },
  { to: '/automatisations/produits', labelKey: 'nav.products', icon: Package, tint: '#ec4899' },
  { to: '/automatisations/statistiques', labelKey: 'nav.automationStats', icon: BarChart3, tint: '#06b6d4' },
  { to: '/automatisations/reglages', labelKey: 'nav.automationSettings', icon: SlidersHorizontal, tint: '#3b82f6' },
  // ── Connexions ────────────────────────────────────────────
  { to: '/connexion-sociale', labelKey: 'nav.social', icon: Plug, tint: '#10b981', groupKey: 'nav.groups.connections' },
  { to: '/integrations', labelKey: 'nav.integrations', icon: Cable, tint: '#f59e0b' },
  // ── Profil ────────────────────────────────────────────────
  { to: '/profil', labelKey: 'nav.profile', icon: UserIcon, tint: '#6b7280', groupKey: 'nav.profile' },
];

// Lot 7 (2026-10-29) — tiroir « Plus » d'un espace M&R : contenu, automatisations, connexions (calendrier et comptes selon la publication), profil.
const MR_DRAWER_ENTRIES: DrawerEntry[] = [
  { to: '/contenu', labelKey: 'nav.content', icon: CalendarDays, tint: '#f59e0b', groupKey: 'nav.groups.content' },
  { to: '/studio', labelKey: 'nav.studio', icon: Wand2, tint: '#ec4899' },
  { to: '/bibliotheque', labelKey: 'nav.library', icon: BookOpen, tint: '#06b6d4' },
  { to: '/identite', labelKey: 'nav.identity', icon: Palette, tint: '#3b82f6' },
  { to: '/calendrier', labelKey: 'nav.calendar', icon: CalendarDays, tint: '#a855f7' },
  { to: '/automatisations', labelKey: 'nav.automations', icon: Zap, tint: '#8b5cf6', groupKey: 'nav.groups.automations' },
  { to: '/automatisations/modeles', labelKey: 'nav.automationTemplates', icon: LayoutTemplate, tint: '#a855f7' },
  { to: '/automatisations/produits', labelKey: 'nav.products', icon: Package, tint: '#ec4899' },
  { to: '/automatisations/statistiques', labelKey: 'nav.automationStats', icon: BarChart3, tint: '#06b6d4' },
  { to: '/automatisations/reglages', labelKey: 'nav.automationSettings', icon: SlidersHorizontal, tint: '#3b82f6' },
  { to: '/comptes', labelKey: 'nav.accounts', icon: Plug, tint: '#10b981', groupKey: 'nav.groups.connections' },
  { to: '/connexion-sociale', labelKey: 'nav.social', icon: Plug, tint: '#10b981' },
  { to: '/integrations', labelKey: 'nav.integrations', icon: Cable, tint: '#f59e0b' },
  { to: '/profil', labelKey: 'nav.profile', icon: UserIcon, tint: '#6b7280', groupKey: 'nav.profile' },
];

export function BottomTabBar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const mrSpace = user?.origin === 'mr_crm'; // lot 7 — espace M&R : vue allégée
  const { meta_features_enabled: metaEnabled, publishing_enabled: publishingEnabled, automations_enabled: automationsEnabled } = useFeatures();
  // Lot 6 (2026-10-29) — même allow-list que la barre latérale.
  const access = useAutomationsAccess(automationsEnabled === true);
  const automationsVisible = automationsEnabled === true && access.data?.enabled === true;
  // Lot A (2026-07-20) — META masqué : « Comptes sociaux » retiré du drawer.
  // Lot 1a (2026-10-28) — Automatisations seulement si le drapeau est allumé.
  const drawerEntries = mrSpace
    ? MR_DRAWER_ENTRIES.filter(
        (e) => (metaEnabled || (e.to !== '/connexion-sociale' && e.to !== '/integrations')) && (publishingEnabled || (e.to !== '/calendrier' && e.to !== '/comptes')) && (automationsVisible || !e.to.startsWith('/automatisations')),
      )
    : DRAWER_ENTRIES.filter(
        (e) => (metaEnabled || e.to !== '/connexion-sociale') && (automationsVisible || !e.to.startsWith('/automatisations')),
      );

  const moreActive = drawerEntries.some((e) => location.pathname.startsWith(e.to));

  function go(to: string): void {
    setDrawerOpen(false);
    navigate(to);
  }

  return (
    <>
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pb-[max(env(safe-area-inset-bottom),0.5rem)] sm:hidden"
      >
        <nav
          className="pointer-events-auto flex h-[62px] w-full max-w-[440px] items-center rounded-[28px] border border-white/70 bg-white px-2 shadow-[0_18px_44px_-18px_rgba(10,18,38,0.22),0_8px_18px_-10px_rgba(168,139,250,0.30),inset_0_1px_0_rgba(255,255,255,0.95)]"
          aria-label={t('nav.mobileLabel') as string}
        >
          {(mrSpace ? MR_TABS(automationsVisible) : PRIMARY_TABS).map((tab) => (
            <TabButton key={tab.to} tab={tab} />
          ))}
          <MoreTab
            onClick={() => setDrawerOpen(true)}
            active={moreActive}
            label={t('nav.shortMore') as string}
          />
        </nav>
      </div>

      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 z-40 bg-night/45 sm:hidden"
              aria-hidden
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 32, stiffness: 360 }}
              drag="y"
              dragDirectionLock
              dragConstraints={{ top: 0, bottom: 600 }}
              dragElastic={{ top: 0, bottom: 0.4 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 120 || info.velocity.y > 500) {
                  setDrawerOpen(false);
                }
              }}
              role="dialog"
              aria-modal="true"
              className="fixed inset-x-0 bottom-0 z-50 max-h-[82vh] overflow-y-auto rounded-t-[28px] bg-white pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-2 shadow-[0_-22px_60px_-12px_rgba(10,18,38,0.30)] sm:hidden"
            >
              <div className="mx-auto mb-3 mt-1 h-[5px] w-[44px] rounded-full bg-ink/15" />
              <div className="flex items-center justify-between px-5 pb-3">
                <p className="font-display text-[17px] font-800 tracking-[-0.02em] text-ink">
                  {t('nav.more')}
                </p>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="-mr-1.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink/[0.06] text-ink/65 transition active:scale-95 active:bg-ink/[0.10]"
                  aria-label={t('common.close') as string}
                >
                  <X size={16} strokeWidth={2.4} />
                </button>
              </div>
              <ul className="space-y-1 px-3">
                {drawerEntries.map((entry) => (
                  <span key={entry.to}>
                    {entry.groupKey && (
                      <li className="px-3 pb-1 pt-3 first:pt-1">
                        <p className="font-mono text-[10px] font-700 uppercase tracking-[0.15em] text-ink/40">
                          {t(entry.groupKey)}
                        </p>
                      </li>
                    )}
                    <DrawerRow
                      entry={entry}
                      active={location.pathname === entry.to}
                      onClick={() => go(entry.to)}
                    />
                  </span>
                ))}
              </ul>
              <div className="mx-3 my-3 h-px bg-ink/[0.07]" />
              <ul className="px-3 pb-2">
                <li>
                  <button
                    onClick={() => {
                      setDrawerOpen(false);
                      logout();
                    }}
                    className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition active:scale-[0.99] active:bg-pink/[0.06]"
                  >
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-pink/10 text-pink">
                      <LogOut size={18} strokeWidth={2.25} />
                    </span>
                    <span className="font-display text-[15px] font-700 tracking-[-0.01em] text-pink">
                      {t('nav.logout')}
                    </span>
                  </button>
                </li>
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── Tab buttons ─────────────────────────────────────────────────────────────

function TabButton({ tab }: { tab: Tab }) {
  const { t } = useTranslation();
  const Icon = tab.icon;
  return (
    <NavLink
      to={tab.to}
      end={tab.to === '/'}
      className="relative flex h-full flex-1 items-center justify-center px-1"
    >
      {({ isActive }) => (
        <TabInner
          icon={<Icon size={22} strokeWidth={isActive ? 2.5 : 2.1} />}
          label={t(tab.labelKey)}
          tint={tab.tint}
          active={isActive}
        />
      )}
    </NavLink>
  );
}

function MoreTab({
  onClick,
  active,
  label,
}: {
  onClick: () => void;
  active: boolean;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className="relative flex h-full flex-1 items-center justify-center px-1 transition"
      aria-label={label}
    >
      <TabInner
        icon={<Plus size={22} strokeWidth={active ? 2.5 : 2.1} />}
        label={label}
        tint="#6b7280"
        active={active}
      />
    </button>
  );
}

/** Shared visual primitive used by both NavLink and More buttons.
 *  - 48px tall pill area with consistent vertical rhythm
 *  - Pill background absolute-fills the cell so all tabs align
 *  - Icon + label stacked, icon 22px, label 10.5px tabular */
function TabInner({
  icon,
  label,
  tint,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  tint: string;
  active: boolean;
}) {
  return (
    <span className="relative flex h-[50px] w-full items-center justify-center transition-transform active:scale-95">
      {active && (
        <motion.span
          layoutId="bottom-tab-pill"
          transition={{ type: 'spring', damping: 30, stiffness: 420 }}
          className="absolute inset-x-1 inset-y-0 rounded-[18px]"
          style={{
            background: `linear-gradient(180deg, ${tint}26, ${tint}12)`,
            boxShadow: `inset 0 0 0 1px ${tint}44, 0 4px 10px -6px ${tint}88`,
          }}
        />
      )}
      <span className="relative z-10 flex flex-col items-center gap-[3px]">
        <span
          className="flex h-6 w-6 items-center justify-center"
          style={{ color: active ? tint : 'rgba(10,18,38,0.42)' }}
        >
          {icon}
        </span>
        <span
          className="whitespace-nowrap text-[10px] leading-none tracking-[-0.005em]"
          style={{
            color: active ? tint : 'rgba(10,18,38,0.55)',
            fontWeight: active ? 800 : 600,
          }}
        >
          {label}
        </span>
      </span>
    </span>
  );
}

// ─── Drawer row ──────────────────────────────────────────────────────────────

function DrawerRow({
  entry,
  active,
  onClick,
}: {
  entry: DrawerEntry;
  active: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const Icon = entry.icon;
  return (
    <li>
      <button
        onClick={onClick}
        className={`group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition active:scale-[0.99] ${
          active ? 'bg-ink/[0.04]' : 'active:bg-ink/[0.04]'
        }`}
      >
        <span
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-white"
          style={{
            background: `linear-gradient(135deg, ${entry.tint}, ${entry.tint}cc)`,
            boxShadow: `0 6px 14px -6px ${entry.tint}`,
          }}
        >
          <Icon size={18} strokeWidth={2.25} />
        </span>
        <span className="flex-1 font-display text-[15px] font-700 leading-none tracking-[-0.01em] text-ink">
          {t(entry.labelKey)}
        </span>
        {active && (
          <span className="mr-2 flex h-2 w-2">
            <Sparkles size={12} className="text-ink/35" />
          </span>
        )}
        <ChevronRight size={16} className="shrink-0 text-ink/30" />
      </button>
    </li>
  );
}
